import { useEffect, useState } from "react";
import {
  Typography,
  CircularProgress,
  Box,
  Tabs,
  Tab,
  Stack,
  Pagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import BatchCard from "./BatchCard";
import SingleOrderCard from "./SingleOrderCard";
import AllOrders from "./AllOrders";
import { getBatches, getSingleOrders, getAllOrders, getLiveOrders } from "../../api/adminApi";

// 👇 UPDATED: Adjusted the badge size and position to be perfectly responsive
const pulsingBadgeStyle = {
  "& .MuiBadge-badge": {
    backgroundColor: "#E85D3F",
    color: "#E85D3F",
    boxShadow: "0 0 0 1.5px #FFFFFF",
    width: "8px",      // Explicitly control dot width
    height: "8px",     // Explicitly control dot height
    minWidth: "8px",
    right: "0px",     // Bring it closer to the text
    top: "6px",
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
};

export default function BatchPage() {
  const [batches, setBatches] = useState([]);
  const [singleOrders, setSingleOrders] = useState([]);
  const [liveOrders, setLiveOrders] = useState([]);
  const [allOrdersList, setAllOrdersList] = useState([]);

  // Modal State for viewing items
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Pagination counts from backend
  const [totalAllPages, setTotalAllPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);

  const [tab, setTab] = useState(0);

  const [pageBatches, setPageBatches] = useState(1);
  const [pageOrders, setPageOrders] = useState(1);
  const [pageLive, setPageLive] = useState(1);
  const [pageAll, setPageAll] = useState(1);
  const itemsPerPage = 10;

  const fetchActiveData = async () => {
    try {
      const [batchRes, singleRes, liveRes] = await Promise.all([
        getBatches(),
        getSingleOrders(),
        getLiveOrders()
      ]);
      setBatches(batchRes.data || []);
      setSingleOrders(singleRes.data || []);
      setLiveOrders(liveRes.data || []);
    } catch (err) {
      console.error("Active Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async (page = 1) => {
    setLoadingHistory(true);
    try {
      const skip = (page - 1) * itemsPerPage;
      const res = await getAllOrders(skip, itemsPerPage);

      setAllOrdersList(res.data.orders || []);
      setTotalAllPages(Math.ceil(res.data.total / itemsPerPage));
      setHistoryFetched(true);
    } catch (err) {
      console.error("History Fetch error:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchActiveData();
    const interval = setInterval(fetchActiveData, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (tab === 3 && !historyFetched) {
      fetchHistoryData(1);
    }
  }, [tab, historyFetched]);

  const activeBatches = batches.filter(
    (b) => !["DELIVERED", "COMPLETED", "CANCELLED"].includes(b.status)
  );
  const activeSingleOrders = singleOrders.filter(
    (o) => !["DELIVERED", "CANCELLED", "REJECTED"].includes(o.status)
  );
  const activeLiveOrders = liveOrders.filter(
    (o) => !["DELIVERED", "CANCELLED", "REJECTED"].includes(o.status)
  );

  const sortedBatches = [...activeBatches].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const sortedOrders = [...activeSingleOrders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const sortedLiveOrders = [...activeLiveOrders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const totalBatchPages = Math.ceil(sortedBatches.length / itemsPerPage);
  const currentBatches = sortedBatches.slice((pageBatches - 1) * itemsPerPage, pageBatches * itemsPerPage);

  const totalOrderPages = Math.ceil(sortedOrders.length / itemsPerPage);
  const currentOrders = sortedOrders.slice((pageOrders - 1) * itemsPerPage, pageOrders * itemsPerPage);

  const totalLivePages = Math.ceil(sortedLiveOrders.length / itemsPerPage);
  const currentLiveOrders = sortedLiveOrders.slice((pageLive - 1) * itemsPerPage, pageLive * itemsPerPage);

  const handleBatchPageChange = (e, val) => { setPageBatches(val); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const handleOrderPageChange = (e, val) => { setPageOrders(val); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const handleLivePageChange = (e, val) => { setPageLive(val); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const handleAllPageChange = (e, val) => {
    setPageAll(val);
    fetchHistoryData(val);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const paginationStyle = {
    '& .Mui-selected': {
      bgcolor: '#F8C332 !important',
      color: '#3A2C23',
      fontWeight: 'bold'
    },
    '& .MuiPaginationItem-root': {
      color: '#6E655E',
      fontWeight: '700'
    }
  };

  const handleOpenModal = (order) => {
    setSelectedOrder(order);
  };

  if (loading) return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="70vh" sx={{ width: "100%" }}>
      <CircularProgress size={56} thickness={4} sx={{ color: '#F8C332' }} />
      <Typography variant="body1" sx={{ mt: 2, fontWeight: 800, color: '#3A2C23', letterSpacing: 0.5 }}>
        Loading active orders...
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", boxSizing: "border-box", minWidth: 0, overflowX: "hidden", pb: { xs: 12, sm: 4 } }}>

      <Typography variant="h5" fontWeight="900" mb={3} sx={{ color: '#3A2C23', letterSpacing: '-0.5px' }}>
        Orders & Batches
      </Typography>

      <Box
        sx={{
          width: "100%",
          bgcolor: "#FFFFFF",
          borderRadius: "20px",
          p: 1,
          mb: 4,
          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, val) => setTab(val)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            "& .MuiTabs-flexContainer": { gap: { xs: 0.5, sm: 1.5 } },
            "& .MuiTab-root": {
              fontWeight: "900",
              textTransform: "none",
              minWidth: 0,

              // 👇 UPDATED: Responsive styling for mobile devices 👇
              px: { xs: 0.5, sm: 2 }, // Smaller padding on mobile
              py: { xs: 1, sm: 1.2 },
              minHeight: "40px",
              fontSize: { xs: "0.7rem", sm: "0.9rem" }, // Smaller font size on mobile
              whiteSpace: "nowrap", // Keeps text strictly on one line
              // 👆 END UPDATED AREA 👆

              borderRadius: "14px",
              color: "#A59D96",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": { color: "#3A2C23", bgcolor: "#FFFDF8" },
              "&.Mui-selected": { color: "#3A2C23", bgcolor: "#F8C332", boxShadow: "0 4px 12px rgba(248, 195, 50, 0.3)" }
            }
          }}
        >
          <Tab
            label={
              <Badge variant="dot" invisible={activeLiveOrders.length === 0} sx={pulsingBadgeStyle}>
                {/* 👈 FIXED: Added padding-right (pr: 1.5) to give the dot safe space */}
                <Box component="span" sx={{ pr: 1.5 }}>Live({activeLiveOrders.length})</Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge variant="dot" invisible={activeSingleOrders.length === 0} sx={pulsingBadgeStyle}>
                <Box component="span" sx={{ pr: 1.5 }}>Single ({activeSingleOrders.length})</Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge variant="dot" invisible={activeBatches.length === 0} sx={pulsingBadgeStyle}>
                <Box component="span" sx={{ pr: 1.5 }}>Batches ({activeBatches.length})</Box>
              </Badge>
            }
          />
          <Tab label="Delivered" />
        </Tabs>
      </Box>

      {/* LIVE ORDERS */}
      {tab === 0 && (
        <Box>
          <Stack spacing={2}>
            {currentLiveOrders.length === 0 ? (
              <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF', mt: 2 }}>
                <Typography color="text.secondary" fontWeight="700">No live orders processing.</Typography>
              </Paper>
            ) : (
              currentLiveOrders.map((o) => (
                <SingleOrderCard
                  key={o.order_id}
                  order={o}
                  isLive={true}
                  onViewItems={() => handleOpenModal(o)}
                  onRefresh={fetchActiveData}
                />
              ))
            )}
          </Stack>
          {totalLivePages > 1 && (
            <Box display="flex" justifyContent="center" mt={4} pb={2}>
              <Pagination count={totalLivePages} page={pageLive} onChange={handleLivePageChange} sx={paginationStyle} />
            </Box>
          )}
        </Box>
      )}

      {/* SINGLE ORDERS */}
      {tab === 1 && (
        <Box>
          <Stack spacing={2}>
            {currentOrders.length === 0 ? (
              <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF', mt: 2 }}>
                <Typography color="text.secondary" fontWeight="700">No active single orders.</Typography>
              </Paper>
            ) : (
              currentOrders.map((o) => (
                <SingleOrderCard
                  key={o.order_id}
                  order={o}
                  onViewItems={() => handleOpenModal(o)}
                  onRefresh={fetchActiveData}
                />
              ))
            )}
          </Stack>
          {totalOrderPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4} pb={2}>
              <Pagination count={totalOrderPages} page={pageOrders} onChange={handleOrderPageChange} sx={paginationStyle} />
            </Box>
          )}
        </Box>
      )}

      {/* BATCHES */}
      {tab === 2 && (
        <Box>
          <Stack spacing={2}>
            {currentBatches.length === 0 ? (
              <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF', mt: 2 }}>
                <Typography color="text.secondary" fontWeight="700">No active batches.</Typography>
              </Paper>
            ) : (
              currentBatches.map((b) => <BatchCard key={b.batch_id} batch={b} />)
            )}
          </Stack>
          {totalBatchPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4} pb={2}>
              <Pagination count={totalBatchPages} page={pageBatches} onChange={handleBatchPageChange} sx={paginationStyle} />
            </Box>
          )}
        </Box>
      )}

      {/* ALL ORDERS */}
      {tab === 3 && (
        <Box>
          {loadingHistory ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" mt={6} minHeight="200px">
              <CircularProgress size={44} thickness={4} sx={{ color: '#F8C332' }} />
              <Typography variant="body1" fontWeight="800" sx={{ color: '#3A2C23', mt: 2 }}>Fetching historical data...</Typography>
            </Box>
          ) : (
            <>
              <Stack spacing={2}>
                {allOrdersList.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF', mt: 2 }}>
                    <Typography color="text.secondary" fontWeight="700">No past orders found.</Typography>
                  </Paper>
                ) : (
                  allOrdersList.map((o) => (
                    <AllOrders key={o.order_id} order={o} onViewItems={() => handleOpenModal(o)} />
                  ))
                )}
              </Stack>
              {totalAllPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4} pb={2}>
                  <Pagination count={totalAllPages} page={pageAll} onChange={handleAllPageChange} sx={paginationStyle} />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* ITEMS MODAL */}
      <Dialog
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', border: '1px solid #ECE5DA', boxShadow: '0 24px 48px rgba(0,0,0,0.1)' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23' }}>
            Order #{selectedOrder?.order_id}
          </Typography>
          <IconButton onClick={() => setSelectedOrder(null)} sx={{ color: '#A59D96' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#ECE5DA', bgcolor: '#FFFDF8', p: 0 }}>
          {selectedOrder?.items && selectedOrder.items.length > 0 ? (
            <List sx={{ width: '100%', py: 0 }}>
              {selectedOrder.items.map((item, index) => (
                <div key={index}>
                  <ListItem sx={{ py: 2, px: 3 }}>
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{ fontWeight: 800, color: '#3A2C23', fontSize: '1rem' }}
                    />
                    <Box sx={{ bgcolor: 'rgba(248, 195, 50, 0.15)', px: 2, py: 0.5, borderRadius: '8px' }}>
                      <Typography fontWeight="900" sx={{ color: '#D97706', fontSize: '1rem' }}>
                        x{item.quantity}
                      </Typography>
                    </Box>
                  </ListItem>
                  {index < selectedOrder.items.length - 1 && <Divider sx={{ borderColor: '#ECE5DA' }} />}
                </div>
              ))}
            </List>
          ) : (
            <Box p={4} textAlign="center">
              <Typography fontWeight="700" color="text.secondary">
                No item details available.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}